<template>
  <div class="ts-cta ts-welcome-audience-cards">
    <div class="row g-4 align-items-stretch">
      <div v-for="card in cards" :key="card.key" class="col-12 col-lg-4 d-flex">
        <article class="ts-welcome-audience-card w-100 d-flex flex-column">
          <div class="ts-cta-icon d-flex flex-shrink-0 align-items-center justify-content-center mb-4">
            <i class="bi" :class="card.icon" aria-hidden="true"></i>
          </div>
          <h2 class="fw-bold mb-2" v-html="card.title"></h2>
          <!-- Info bar texts are HTML from PAT (links, lists) -->
          <div class="ts-welcome-audience-text mb-4" v-html="card.text"></div>
          <a
            v-if="card.href"
            class="btn btn-primary ts-welcome-audience-button w-100 fw-semibold mt-auto"
            :href="card.href"
          >
            {{ card.button }}
          </a>
          <button
            v-else-if="card.button"
            class="btn btn-primary ts-welcome-audience-button w-100 fw-semibold mt-auto"
            type="button"
            @click="register(card.tab || AuthTab.default)"
          >
            {{ card.button }}
          </button>
        </article>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator';
import { AppModule, AuthTab } from '@/store/modules/app';
import { siteContent } from '@/sites';

// Info bar from GET /portal/settings → info_bars (PAT → Fachkräfteportal Einstellungen)
interface InfoBar {
  id: number
  label: string
  info_text: string
  picture: string | null
  // type 'info': text only
  // type 'applicant': applicant registration button
  // type 'company': company registration button
  // type 'partner': contact button for job centres and training providers
  // null: not set in PAT yet → shown like 'info', the first one with the registration button
  type: 'info' | 'applicant' | 'company' | 'partner' | null
}

interface AudienceCard {
  key: string
  icon: string
  title: string
  text: string
  button: string
  tab: AuthTab | null
  href: string
}

@Component
export default class TsAudienceCards extends Vue {
  AuthTab = AuthTab;

  get partnerEmail() {
    return siteContent(AppModule.sid).partnerEmail;
  }

  get infoBars(): InfoBar[] {
    return AppModule.settingPortal.info_bars || [];
  }

  // The site's own info bars from PAT; the default cards only while a site has none
  get cards(): AudienceCard[] {
    if (this.infoBars.length) {
      return this.infoBars.map((bar, index) => this.cardFromInfoBar(bar, index));
    }
    return this.defaultCards;
  }

  get defaultCards(): AudienceCard[] {
    const t = AppModule.translation;
    const cards: AudienceCard[] = [
      {
        key: 'applicant',
        icon: 'bi-person',
        title: t[16587],
        text: t[16588],
        button: t[16589],
        tab: AuthTab.applicant,
        href: '',
      },
      {
        key: 'company',
        icon: 'bi-building',
        title: t[16590],
        text: t[16591],
        button: t[16592],
        tab: AuthTab.firm,
        href: '',
      },
    ];
    // Partner card only when the site has a contact for job centres and training providers
    if (this.partnerEmail) {
      cards.push({
        key: 'partner',
        icon: 'bi-people',
        title: t[16593],
        text: t[16594],
        button: t[16595],
        tab: null,
        href: `mailto:${this.partnerEmail}`,
      });
    }
    return cards;
  }

  cardFromInfoBar(bar: InfoBar, index: number): AudienceCard {
    const t = AppModule.translation;
    const card: AudienceCard = {
      key: `bar-${bar.id}`,
      icon: 'bi-info-circle',
      title: bar.label,
      text: bar.info_text,
      button: '',
      tab: null,
      href: '',
    };
    if (bar.type === 'applicant') {
      return {
        ...card, icon: 'bi-person', button: t[16589], tab: AuthTab.applicant,
      };
    }
    if (bar.type === 'company') {
      return {
        ...card, icon: 'bi-building', button: t[16592], tab: AuthTab.firm,
      };
    }
    if (bar.type === 'partner') {
      const href = this.partnerEmail ? `mailto:${this.partnerEmail}` : '';
      return {
        ...card, icon: 'bi-people', button: href ? t[16595] : '', href,
      };
    }
    // No type yet: like the old right sidebar, the first bar gets the registration button
    if (!bar.type && index === 0) {
      return { ...card, button: t[13782], tab: AuthTab.default };
    }
    return card;
  }

  register(tab: AuthTab) {
    AppModule.SET_SELECTED_AUTH_TAB(tab);
    this.$bvModal.show('register-modal');
  }
}
</script>

<style scoped lang="scss">
// PAT texts come as HTML: keep the card's paragraph look and the accent links
.ts-welcome-audience-text {
  color: var(--ts-text);
}

.ts-welcome-audience-text ::v-deep {
  p:last-child,
  ul:last-child {
    margin-bottom: 0;
  }

  a {
    color: var(--ts-accent-text);
  }
}
</style>
